<template>
  <div>
    <h1 class="mb-4"><v-icon color="primary" large>mdi-cog</v-icon> Configuración</h1>

    <v-tabs v-model="tab" color="primary" class="mb-4">
      <v-tab><v-icon left>mdi-domain</v-icon> Identidad</v-tab>
      <v-tab><v-icon left>mdi-phone</v-icon> Contacto</v-tab>
      <v-tab><v-icon left>mdi-share-variant</v-icon> Redes Sociales</v-tab>
      <v-tab><v-icon left>mdi-palette</v-icon> Apariencia</v-tab>
      <v-tab><v-icon left>mdi-clock-time-four</v-icon> Horarios</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- IDENTIDAD -->
      <v-tab-item>
        <v-card rounded="xl" class="pa-4 elevation-3">
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="datos.nombre" label="Nombre del gimnasio" outlined :rules="[r => !!r || 'Requerido']" />
              <v-text-field v-model="datos.eslogan" label="Eslogan o lema" outlined hint="Aparece en credenciales y reportes" persistent-hint />
              <v-select v-model="datos.moneda" :items="monedas" label="Moneda" outlined />
            </v-col>
            <v-col cols="12" md="4" class="text-center">
              <v-avatar size="120" class="elevation-6 mb-3">
                <img v-if="imagenPreview" :src="imagenPreview" style="object-fit:contain;" />
                <v-icon v-else size="80" color="grey">mdi-image-plus</v-icon>
              </v-avatar>
              <v-file-input
                accept="image/png,image/jpeg,image/webp"
                label="Logo del gimnasio"
                outlined dense prepend-icon="mdi-camera"
                v-model="archivoLogo"
                @change="onLogoChange"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-tab-item>

      <!-- CONTACTO -->
      <v-tab-item>
        <v-card rounded="xl" class="pa-4 elevation-3">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field v-model="datos.telefono" label="Teléfono" outlined prepend-inner-icon="mdi-phone" />
              <v-text-field v-model="datos.correo" label="Correo electrónico" outlined prepend-inner-icon="mdi-email" type="email" />
              <v-text-field v-model="datos.sitio_web" label="Sitio web" outlined prepend-inner-icon="mdi-web" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="datos.direccion" label="Dirección" outlined prepend-inner-icon="mdi-map-marker" />
              <v-text-field v-model="datos.ciudad" label="Ciudad" outlined prepend-inner-icon="mdi-city" />
              <v-text-field v-model="datos.pais" label="País" outlined prepend-inner-icon="mdi-earth" />
            </v-col>
          </v-row>
        </v-card>
      </v-tab-item>

      <!-- REDES SOCIALES -->
      <v-tab-item>
        <v-card rounded="xl" class="pa-4 elevation-3">
          <v-text-field v-model="datos.instagram" label="Instagram (usuario o URL)" outlined prepend-inner-icon="mdi-instagram" class="mb-2" />
          <v-text-field v-model="datos.facebook" label="Facebook (URL de la página)" outlined prepend-inner-icon="mdi-facebook" class="mb-2" />
          <v-text-field v-model="datos.whatsapp" label="WhatsApp (número con código de país)" outlined prepend-inner-icon="mdi-whatsapp" hint="Ej: +50688887777" persistent-hint />
        </v-card>
      </v-tab-item>

      <!-- APARIENCIA -->
      <v-tab-item>
        <v-card rounded="xl" class="pa-4 elevation-3">
          <v-row>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text">Color primario</label>
              <div class="d-flex align-center mt-1">
                <input type="color" v-model="datos.color_primario" style="width:48px;height:48px;border:none;cursor:pointer;border-radius:8px;" />
                <v-text-field v-model="datos.color_primario" outlined dense hide-details class="ml-2" style="max-width:130px;" />
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text">Color secundario</label>
              <div class="d-flex align-center mt-1">
                <input type="color" v-model="datos.color_secundario" style="width:48px;height:48px;border:none;cursor:pointer;border-radius:8px;" />
                <v-text-field v-model="datos.color_secundario" outlined dense hide-details class="ml-2" style="max-width:130px;" />
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text">Color acento</label>
              <div class="d-flex align-center mt-1">
                <input type="color" v-model="datos.color_acento" style="width:48px;height:48px;border:none;cursor:pointer;border-radius:8px;" />
                <v-text-field v-model="datos.color_acento" outlined dense hide-details class="ml-2" style="max-width:130px;" />
              </div>
            </v-col>
          </v-row>

          <!-- Preview en tiempo real -->
          <v-divider class="my-4" />
          <p class="text-subtitle-2 mb-2">Vista previa</p>
          <div class="d-flex flex-wrap gap-2">
            <v-btn :color="datos.color_primario" dark rounded small elevation="3">Primario</v-btn>
            <v-btn :color="datos.color_secundario" dark rounded small elevation="3">Secundario</v-btn>
            <v-btn :color="datos.color_acento" dark rounded small elevation="3">Acento</v-btn>
          </div>

          <v-divider class="my-4" />
          <v-switch v-model="modoOscuro" label="Modo oscuro por defecto" color="primary" @change="onModoOscuro" />
        </v-card>
      </v-tab-item>

      <!-- HORARIOS -->
      <v-tab-item>
        <v-card rounded="xl" class="pa-4 elevation-3">
          <v-row v-for="(dia, i) in diasSemana" :key="i" class="mb-2" align="center">
            <v-col cols="3" sm="2">
              <v-switch v-model="horario[dia.key].abierto" :label="dia.label" dense hide-details />
            </v-col>
            <v-col cols="4" sm="3">
              <v-text-field type="time" v-model="horario[dia.key].apertura" label="Apertura" outlined dense hide-details :disabled="!horario[dia.key].abierto" />
            </v-col>
            <v-col cols="1" class="text-center">-</v-col>
            <v-col cols="4" sm="3">
              <v-text-field type="time" v-model="horario[dia.key].cierre" label="Cierre" outlined dense hide-details :disabled="!horario[dia.key].abierto" />
            </v-col>
          </v-row>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Botón guardar flotante -->
    <v-btn
      color="primary" x-large fab fixed right bottom
      elevation="12" @click="guardar" :loading="guardando"
    >
      <v-icon>mdi-content-save</v-icon>
    </v-btn>

    <v-snackbar v-model="snack" :color="snackColor" top timeout="3000">{{ snackMsg }}</v-snackbar>

    <v-overlay :value="cargando"><v-progress-circular indeterminate size="64" /></v-overlay>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import Utiles from '../../Servicios/Utiles'
import { aplicarTema } from '../../plugins/tema'

export default {
  name: 'FormConfiguracion',
  data: () => ({
    tab: 0,
    cargando: false,
    guardando: false,
    snack: false, snackMsg: '', snackColor: 'success',
    archivoLogo: null,
    imagenPreview: null,
    logoCambia: false,
    modoOscuro: false,
    datos: {
      nombre: '', eslogan: '', telefono: '', correo: '', sitio_web: '',
      direccion: '', ciudad: '', pais: '', logo: '',
      color_primario: '#1706FF', color_secundario: '#0D47A1', color_acento: '#00BCD4',
      modo_oscuro: 0, moneda: '$',
      instagram: '', facebook: '', whatsapp: '',
    },
    monedas: ['$', '₡', '€', '£', 'Q', 'B/.', 'Bs.', 'S/.'],
    diasSemana: [
      { key: 'lunes', label: 'Lun' }, { key: 'martes', label: 'Mar' },
      { key: 'miercoles', label: 'Mié' }, { key: 'jueves', label: 'Jue' },
      { key: 'viernes', label: 'Vie' }, { key: 'sabado', label: 'Sáb' },
      { key: 'domingo', label: 'Dom' },
    ],
    horario: {
      lunes: { abierto: true, apertura: '06:00', cierre: '22:00' },
      martes: { abierto: true, apertura: '06:00', cierre: '22:00' },
      miercoles: { abierto: true, apertura: '06:00', cierre: '22:00' },
      jueves: { abierto: true, apertura: '06:00', cierre: '22:00' },
      viernes: { abierto: true, apertura: '06:00', cierre: '22:00' },
      sabado: { abierto: true, apertura: '08:00', cierre: '18:00' },
      domingo: { abierto: false, apertura: '09:00', cierre: '13:00' },
    },
  }),
  mounted() { this.cargar() },
  methods: {
    async cargar() {
      this.cargando = true
      const aj = await HttpService.obtenerConDatos({ metodo: 'obtener' }, 'ajustes.php')
      if (aj) {
        this.datos = { ...this.datos, ...aj }
        this.modoOscuro = !!(parseInt(aj.modo_oscuro))
        this.imagenPreview = Utiles.generarURL(aj.logo)
        if (aj.horario) {
          try { const h = JSON.parse(aj.horario); if (h) this.horario = { ...this.horario, ...h } } catch (e) {}
        }
      }
      this.cargando = false
    },
    onLogoChange() {
      if (!this.archivoLogo) return
      const reader = new FileReader()
      reader.onload = e => { this.imagenPreview = e.target.result; this.logoCambia = true }
      reader.readAsDataURL(this.archivoLogo)
    },
    onModoOscuro(val) {
      this.$vuetify.theme.dark = val
    },
    async guardar() {
      this.guardando = true
      const payload = {
        ...this.datos,
        logo: this.logoCambia ? this.imagenPreview : this.datos.logo,
        logoCambia: this.logoCambia,
        modo_oscuro: this.modoOscuro ? 1 : 0,
        horario: JSON.stringify(this.horario),
      }
      const ok = await HttpService.obtenerConDatos({ metodo: 'registrar', ajustes: payload }, 'ajustes.php')
      if (ok) {
        // Actualizar localStorage y tema
        localStorage.setItem('nombreGimnasio', this.datos.nombre)
        localStorage.setItem('logoGimnasio', this.datos.logo)
        localStorage.setItem('monedaGimnasio', this.datos.moneda)
        localStorage.setItem('ajustes', JSON.stringify(payload))
        aplicarTema(this.$vuetify, payload)
        this.snackMsg = '¡Configuración guardada exitosamente!'
        this.snackColor = 'success'
        this.logoCambia = false
      } else {
        this.snackMsg = 'Error al guardar. Intenta de nuevo.'
        this.snackColor = 'error'
      }
      this.snack = true
      this.guardando = false
    },
  },
}
</script>


// WEBPACK FOOTER //
// src/components/Configuracion/FormConfiguracion.vue